import { Select, TextInput } from '@octano/global-ui';
import { useEffect, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Col, Row } from 'reactstrap';
import { TranslationsKeys } from '../../../../locales/translations';
import SectionTitle from '../../../Text/SectionTitle';

const CHILE_LOWER_CASE = 'chile';

type ContactSectionProps = {
  prefix: string;
  fieldNames?: {
    businessAddressStreet: string;
    businessAddressNumber: string;
    businessAddressExtra: string;
    businessCountry: string;
    businessRegion: string;
    businessCommune: string;
    businessCity: string;
    businessPhone: string;
    businessCellPhone: string;
    businessEmail: string;
    businessCustomCity: string;
    businessLocation: string;
  };
  disabled?: boolean;
  hasEmail?: boolean;
};

/**
 * Para usar este componente tu formulario debe estar envuelto en su FormProvider
 * para poder acceder a la información del formulario a través de useFormContext
 * Los nombres agregados en fieldNames se relacionan a los campos que utilizaste
 * al definir tu formulario.
 */
const DynamicContactBusiness = ({
  prefix,
  fieldNames = {
    businessAddressStreet: 'businessAddressStreet',
    businessAddressNumber: 'businessAddressNumber',
    businessAddressExtra: 'businessAddressExtra',
    businessCountry: 'businessCountry',
    businessRegion: 'businessRegion',
    businessCommune: 'businessCommune',
    businessPhone: 'businessPhone',
    businessCellPhone: 'businessCellPhone',
    businessEmail: 'businessEmail',
    businessLocation: 'businessLocation',
    businessCity: 'businessCity',
    businessCustomCity: 'businessCustomCity',
  },
  disabled = false,
  hasEmail = false,
}: ContactSectionProps) => {
  const { control, watch, formState, setValue } = useFormContext();
  const { t } = useTranslation(
    TranslationsKeys.STUDENT_FILE_PERSONAL_INFORMATION,
  );

  const selectedRegion = watch(fieldNames.businessRegion);
  const selectedCountry = watch(fieldNames.businessCountry);
  const rut = watch('rut');

  const isRegionTouched = formState.touchedFields[fieldNames.businessRegion];

  const isChile = useMemo(() => {
    return String(selectedCountry?.label)
      .toLowerCase()
      .includes(CHILE_LOWER_CASE);
  }, [selectedCountry?.label]);

  useEffect(() => {
    if (isRegionTouched) {
      setValue(fieldNames.businessCommune, null);
      setValue(fieldNames.businessCity, null);
    }
  }, [
    selectedRegion,
    setValue,
    isRegionTouched,
    fieldNames.businessCommune,
    fieldNames.businessCity,
  ]);

  return (
    <>
      <Row className="pt-5">
        <Col xs={12}>
          <SectionTitle text={t(`${prefix}.title`)} />
        </Col>
      </Row>
      <Row>
        <Col className="pb-3" xs={12} lg={8}>
          <TextInput
            name={fieldNames.businessAddressStreet}
            label={t(`${prefix}.${fieldNames.businessAddressStreet}`)}
            control={control}
            readOnly={disabled}
          />
        </Col>
        <Col className="pb-3" xs={12} lg={4}>
          <TextInput
            name={fieldNames.businessAddressNumber}
            label={t(`${prefix}.${fieldNames.businessAddressNumber}`)}
            control={control}
            readOnly={disabled}
          />
        </Col>
        <Col className="pb-3" xs={12} lg={4}>
          <TextInput
            name={fieldNames.businessAddressExtra}
            label={t(`${prefix}.${fieldNames.businessAddressExtra}`)}
            control={control}
            readOnly={disabled}
          />
        </Col>
        <Col className="pb-3" xs={12} lg={4}>
          <Select
            name={fieldNames.businessCountry}
            label={t(`${prefix}.${fieldNames.businessCountry}`)}
            options={[]}
            control={control}
            disabled={disabled || Boolean(rut)}
          />
        </Col>
        {isChile ? (
          <>
            <Col className="pb-3" xs={12} lg={4}>
              <Select
                name={fieldNames.businessRegion}
                label={t(`${prefix}.${fieldNames.businessRegion}`)}
                options={[]}
                control={control}
                disabled={disabled}
              />
            </Col>
            <Col className="pb-3" xs={12} lg={4}>
              <Select
                name={fieldNames.businessCity}
                label={t(`${prefix}.${fieldNames.businessCity}`)}
                options={[]}
                control={control}
                shouldUnregister
                disabled={disabled}
              />
            </Col>
            <Col className="pb-3" xs={12} lg={4}>
              <Select
                name={fieldNames.businessCommune}
                label={t(`${prefix}.${fieldNames.businessCommune}`)}
                options={[]}
                control={control}
                shouldUnregister
                disabled={disabled}
              />
            </Col>
          </>
        ) : (
          <>
            <Col className="pb-3" xs={12} lg={4}>
              <TextInput
                name={fieldNames.businessLocation}
                label={t(`${prefix}.${fieldNames.businessLocation}`)}
                control={control}
                shouldUnregister
                readOnly={disabled}
              />
            </Col>
            <Col className="pb-3" xs={12} lg={4}>
              <TextInput
                name={fieldNames.businessCustomCity}
                label={t(`${prefix}.${fieldNames.businessCity}`)}
                control={control}
                shouldUnregister
                readOnly={disabled}
              />
            </Col>
          </>
        )}
        <Col className="pb-3" xs={12} lg={4}>
          <TextInput
            name={fieldNames.businessPhone}
            label={t(`${prefix}.${fieldNames.businessPhone}`)}
            control={control}
            formatter="phone"
            placeholder={t(`common.placeholder.phone`)}
            readOnly={disabled}
          />
        </Col>
        <Col className="pb-3" xs={12} lg={4}>
          <TextInput
            name={fieldNames.businessCellPhone}
            label={t(`${prefix}.${fieldNames.businessCellPhone}`)}
            control={control}
            formatter="phone"
            placeholder={t(`common.placeholder.cellphone`)}
            readOnly={disabled}
          />
        </Col>
        {hasEmail ? (
          <Col className="pb-3" xs={12} lg={4}>
            <TextInput
              name={fieldNames.businessEmail}
              label={t(`${prefix}.${fieldNames.businessEmail}`)}
              control={control}
              disabled={disabled}
              readOnly={disabled}
            />
          </Col>
        ) : null}
      </Row>
    </>
  );
};

export default DynamicContactBusiness;
